import { Text, Flex, CheckboxGroup, Switch, Button, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import { InputField } from '@/components/InputField';
import { useCreateAccessControlPermissionTypeMutation } from '../../services/queries/access-control';
import { accessControlPermissionTypesNewBreadcrumb } from './breadcrumb';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { accessControlPermissionTypesNewSchema, AccessControlPermissionTypesNewFormData } from './schemas';
import { Permission } from '@/model';
import { queryClient } from '@/services/api/queryClient';
import { useTranslation } from 'react-i18next'; // Importando hook para tradução

export const AccessControlPermissionTypesNew = () => {
  const { t } = useTranslation(); // Inicializando a função de tradução
  const navigate = useNavigate();
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<AccessControlPermissionTypesNewFormData>({
    defaultValues: {
      name: '',
      permissions: {
        ACCESS_CONTROL: false,
        ACCESS_PLANS: false,
        APPLICATION: false,
        COMMUNICATION_CENTRAL: false,
        FINANCIAL: false,
        HOME: false,
        PARTNERS: false,
        SUBSCRIBERS: false,
        USERS: false,
      },
    },
    resolver: zodResolver(accessControlPermissionTypesNewSchema),
  });
  const toast = useToast();
  const { mutate: createAccessControlPermissionType, isLoading: isLoadingCreateAccessControlPermissionType } =
    useCreateAccessControlPermissionTypeMutation();

  const handlePermissionChange = (permission: Permission, value: boolean) => {
    setValue(`permissions.${permission}`, value as never);
  };

  const onSubmit = (data: AccessControlPermissionTypesNewFormData) => {
    createAccessControlPermissionType(data, {
      onSuccess: async () => {
        toast({
          title: t('pages.access_control.permission_types_new.toast_success'), // Usando tradução
          status: 'success',
        });

        await queryClient.invalidateQueries(['GET_ACCESS_CONTROL_PERMISSION_TYPES']);

        navigate('/access-control/permission-types');
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.access_control.permission_types_new.error_permission_message'), // Usando tradução
        });
      },
    });
  };

  return (
    <PageContent
      title={t('pages.access_control.permission_types_new.title')}
      breadcrumbItems={accessControlPermissionTypesNewBreadcrumb}
    >
      <SectionBox bodyProps={{ gap: 'small' }} mt={8}>
        <Flex direction='column' gap='deka'>
          <Text fontWeight='medium' fontSize='18px'>
            {t('pages.access_control.permission_types_new.form.permission_data')} {/* Usando tradução */}
          </Text>
          <Flex as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap='micro'>
            <Flex>
              <InputField
                withoutFloating
                label={t('pages.access_control.permission_types_new.form.name_label')}
                {...register('name')}
                maxW={{ md: '300px' }}
                rootProps={{ isInvalid: !!errors?.name, errorMessage: errors?.name?.message }}
              />
              <Button type='submit'>{t('pages.access_control.permission_types_new.form.submit_button')}</Button>{' '}
              {/* Usando tradução */}
            </Flex>

            <Flex direction='column' gap={4} alignItems='flex-start'>
              <Text fontWeight='medium' fontSize='18px' mt={6} mb={2}>
                {t('pages.access_control.permission_types_new.form.permissions_section')} {/* Usando tradução */}
              </Text>
              <CheckboxGroup>
                <Switch value={Permission.HOME} onChange={event => handlePermissionChange(Permission.HOME, event.target.checked)}>
                  {t('pages.access_control.permission_types_new.permissions.home')} {/* Usando tradução */}
                </Switch>
                <Switch
                  value={Permission.USERS}
                  onChange={event => handlePermissionChange(Permission.USERS, event.target.checked)}
                >
                  {t('pages.access_control.permission_types_new.permissions.users')} {/* Usando tradução */}
                </Switch>
                <Switch
                  value={Permission.SUBSCRIBERS}
                  onChange={event => handlePermissionChange(Permission.SUBSCRIBERS, event.target.checked)}
                >
                  {t('pages.access_control.permission_types_new.permissions.subscribers')} {/* Usando tradução */}
                </Switch>
                <Switch
                  value={Permission.ACCESS_PLANS}
                  onChange={event => handlePermissionChange(Permission.ACCESS_PLANS, event.target.checked)}
                >
                  {t('pages.access_control.permission_types_new.permissions.access_plans')} {/* Usando tradução */}
                </Switch>
                <Switch
                  value={Permission.APPLICATION}
                  onChange={event => handlePermissionChange(Permission.APPLICATION, event.target.checked)}
                >
                  {t('pages.access_control.permission_types_new.permissions.application')} {/* Usando tradução */}
                </Switch>
                <Switch
                  value={Permission.FINANCIAL}
                  onChange={event => handlePermissionChange(Permission.FINANCIAL, event.target.checked)}
                >
                  {t('pages.access_control.permission_types_new.permissions.financial')} {/* Usando tradução */}
                </Switch>
                <Switch
                  value={Permission.PARTNERS}
                  onChange={event => handlePermissionChange(Permission.PARTNERS, event.target.checked)}
                >
                  {t('pages.access_control.permission_types_new.permissions.partners')} {/* Usando tradução */}
                </Switch>
                <Switch
                  value={Permission.ACCESS_CONTROL}
                  onChange={event => handlePermissionChange(Permission.ACCESS_CONTROL, event.target.checked)}
                >
                  {t('pages.access_control.permission_types_new.permissions.access_control')} {/* Usando tradução */}
                </Switch>
                <Switch
                  value={Permission.COMMUNICATION_CENTRAL}
                  onChange={event => handlePermissionChange(Permission.COMMUNICATION_CENTRAL, event.target.checked)}
                >
                  {t('pages.access_control.permission_types_new.permissions.communication_central')} {/* Usando tradução */}
                </Switch>
              </CheckboxGroup>
              {errors?.permissions ? <Text color='red.500'>{errors?.permissions?.message}</Text> : null}
            </Flex>
          </Flex>
        </Flex>
      </SectionBox>

      <CenteredSpinner isLoading={isLoadingCreateAccessControlPermissionType} />
    </PageContent>
  );
};
